import i18n from '@/core/i18n';
import { BaseStatus, StatusConfig } from '@/modules/common';
import { createStatusOptionsMapper } from '@/modules/common/mappers/status-options.mapper';
import { InvoiceStatus } from '@/modules/finance/invoices/types';

/**
 * Invoice status classes that implement the BaseStatus interface.
 * Each status knows how to configure itself for the StatusBadge component.
 */
export class InvoiceStatusOpen implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-cyan-600 text-cyan-600',
    };
  }
}

export class InvoiceStatusInProgress implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-chart-3 text-chart-3',
    };
  }
}

export class InvoiceStatusError implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-destructive text-destructive',
    };
  }
}

export class InvoiceStatusOverdue implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-destructive text-destructive',
    };
  }
}

export class InvoiceStatusPaid implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-success text-success',
    };
  }
}

export class InvoiceStatusUnknown implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-muted-foreground text-muted-foreground',
    };
  }
}

// Factory function to create status instances based on enum values
export const createInvoiceStatus = (status: string): BaseStatus => {
  switch (status) {
    case InvoiceStatus.Open:
      return new InvoiceStatusOpen();
    case InvoiceStatus.Overdue:
      return new InvoiceStatusOverdue();
    case InvoiceStatus.InProgress:
      return new InvoiceStatusInProgress();
    case InvoiceStatus.Error:
      return new InvoiceStatusError();
    case InvoiceStatus.Paid:
      return new InvoiceStatusPaid();
    default:
      return new InvoiceStatusUnknown(); // Default fallback
  }
};

/**
 * Get localized status text for Invoice status.
 */
export const getInvoiceStatusText = (status: InvoiceStatus): string => {
  switch (status) {
    case InvoiceStatus.Open:
      return i18n.t('authenticated.invoices:status.open');
    case InvoiceStatus.Overdue:
      return i18n.t('authenticated.invoices:status.overdue');
    case InvoiceStatus.InProgress:
      return i18n.t('authenticated.invoices:status.inProgress');
    case InvoiceStatus.Error:
      return i18n.t('authenticated.invoices:status.error');
    case InvoiceStatus.Paid:
      return i18n.t('authenticated.invoices:status.paid');
    default:
      return i18n.t('authenticated.invoices:status.unknown');
  }
};

export const getInvoiceStatusColor = (status: InvoiceStatus): string => {
  switch (status) {
    case InvoiceStatus.Open:
      return 'cyan-600';
    case InvoiceStatus.Overdue:
      return 'destructive';
    case InvoiceStatus.InProgress:
      return 'chart-3';
    case InvoiceStatus.Error:
      return 'destructive';
    case InvoiceStatus.Paid:
      return 'success';
    default:
      return 'muted-foreground';
  }
};

/**
 * Preconfigured mapper for invoice status options.
 * Maps an array of InvoiceStatus values to option objects with label, value, and color.
 */
export const getInvoiceStatusOptions = createStatusOptionsMapper<InvoiceStatus>({
  getStatusColor: getInvoiceStatusColor,
  getStatusText: getInvoiceStatusText,
});
